import { isMobile as _isMobile } from 'react-device-detect';
import { ListingPriceType } from '../definitions';
import {
    LISTING_PROPERTY_SUBTYPE_LABELS,
    ListingPropertyCategory,
    ListingPropertySubtype,
    ListingApartmentRoomCount,
    LISTING_APARTMENT_ROOM_COUNT_MAPPING,
} from '../definitions/propertyTypes';
import { Listing } from '../types';
import { AdvertTxnTypeOption } from '../../adverts/definitions/txnType';
import { ADVERT_TXN_TYPE_OPTIONS } from '../../adverts/definitions/txnType';
import { ListingResponseDtoPriceType } from '@/api/generated/model';
import { mapCurrencyCodeToText } from '@/features/adverts/utils';
import { ListingTxnType } from '../definitions/txnType';
import { formatPrice } from '@/features/adverts/utils';

export function mapPriceTypeToSuffix(
    priceType: ListingResponseDtoPriceType | null | undefined,
): string {
    switch (priceType) {
        case ListingResponseDtoPriceType.PER_SQM:
            return '/m²';
        case ListingResponseDtoPriceType.PER_MONTH:
            return '/mesiac';
        case ListingResponseDtoPriceType.PER_YEAR:
            return '/rok';
        case ListingResponseDtoPriceType.PER_SQM_PER_MONTH:
            return '/m²/mesiac';
        case ListingResponseDtoPriceType.TOTAL:
        default:
            return '';
    }
}

export function formatListingPrice(listing: Listing): string {
    if (listing.priceOnRequest) return 'Cena dohodou';
    if (!listing.finalPrice) return '-';

    const currency = mapCurrencyCodeToText(listing.currencyCode);
    const suffix = mapPriceTypeToSuffix(listing.priceType);

    return `${formatPrice(listing.finalPrice)} ${currency}${suffix ? ` ${suffix}` : ''}`;
}

export function formatListingPricePerM2(listing: Listing): string | null {
    const priceType = listing.priceType;

    if (
        priceType == null ||
        (priceType !== ListingPriceType.TOTAL && priceType !== ListingPriceType.PER_MONTH)
    ) {
        return null;
    }

    const area = resolveListingArea(listing);
    if (!listing.finalPrice || !area) {
        return null;
    }

    if (listing.priceOnRequest || !listing.finalPrice) return null;

    const perM2 = calculatePricePerM2(listing.finalPrice, area);
    if (!perM2) return null;

    const currency = mapCurrencyCodeToText(listing.currencyCode);
    return `${formatPrice(perM2)} ${currency} / m²`;
}

export function mapPropertyCategoryToText(category: ListingPropertyCategory): string {
    switch (category) {
        case 'HOUSE':
            return 'Dom';

        case 'APARTMENT':
            return 'Byt';

        case 'LAND':
            return 'Pozemok';

        case 'RECREATIONAL':
            return 'Rekreačné';

        case 'OTHER':
            return 'Ostatné';

        default:
            return '';
    }
}

export function mapPropertySubTypeToText(type: ListingPropertySubtype): string {
    return LISTING_PROPERTY_SUBTYPE_LABELS[type] || '';
}

export function resolveApartmentRoomCountType(listing: Listing): ListingApartmentRoomCount | null {
    const roomCount = Number(listing.detail?.roomCount);

    if (!Number.isFinite(roomCount)) return null;

    const entry = Object.entries(LISTING_APARTMENT_ROOM_COUNT_MAPPING).find(([, roomCounts]) =>
        (roomCounts as readonly number[]).includes(roomCount),
    );

    return (entry?.[0] as ListingApartmentRoomCount) ?? null;
}

export function resolveListingSubtype(listing: Listing): ListingPropertySubtype | null {
    const d = listing.detail;

    if (!d) return null;

    switch (listing.propertyCategory) {
        case ListingPropertyCategory.HOUSE:
            return d.houseSubtype ? `${ListingPropertyCategory.HOUSE}_${d.houseSubtype}` : null;

        case ListingPropertyCategory.APARTMENT: {
            const roomType = resolveApartmentRoomCountType(listing);

            if (roomType) {
                return `${ListingPropertyCategory.APARTMENT}_${roomType}`;
            }

            return d.apartmentSubtype
                ? `${ListingPropertyCategory.APARTMENT}_${d.apartmentSubtype}`
                : null;
        }

        case ListingPropertyCategory.LAND:
            return d.landSubtype ? `${ListingPropertyCategory.LAND}_${d.landSubtype}` : null;

        case ListingPropertyCategory.RECREATIONAL:
            return d.recreationalSubtype
                ? `${ListingPropertyCategory.RECREATIONAL}_${d.recreationalSubtype}`
                : null;

        case ListingPropertyCategory.OTHER:
            return d.otherSubtype ? `${ListingPropertyCategory.OTHER}_${d.otherSubtype}` : null;

        default:
            return null;
    }
}

export function resolveListingArea(listing: Listing): number | null {
    const d = listing.detail;

    if (!d) return null;

    switch (listing.propertyCategory) {
        case ListingPropertyCategory.APARTMENT:
            return d.usableArea ?? null;

        case ListingPropertyCategory.HOUSE:
            return d.usableArea ?? null;

        case ListingPropertyCategory.LAND:
            return d.landArea ?? null;

        case ListingPropertyCategory.RECREATIONAL:
            return d.usableArea ?? null;

        case ListingPropertyCategory.OTHER:
        default:
            return d.usableArea ?? null;
    }
}

export function resolveListingType(listing: Listing): AdvertTxnTypeOption | null {
    if (!listing) return null;

    switch (listing.type) {
        case ListingTxnType.RENT:
            return ADVERT_TXN_TYPE_OPTIONS.find((i) => i.key === ListingTxnType.RENT) ?? null;

        case ListingTxnType.SALE:
            return ADVERT_TXN_TYPE_OPTIONS.find((i) => i.key === ListingTxnType.SALE) ?? null;
    }
}

/*
export function calculatePricePerM2(priceAmount?: number, areaM2?: number): number | null {
    if (!priceAmount || !areaM2 || areaM2 === 0) return null;

    return Math.round((priceAmount / areaM2) * 100) / 100;
}
*/

export function calculatePricePerM2(
    price: number | null | undefined,
    area: number | string | null | undefined,
): number | null {
    if (!price || !area) {
        return null;
    }

    const numericArea = Number(area);

    if (!numericArea || numericArea <= 0) {
        return null;
    }

    return Number((price / numericArea).toFixed(2));
}

export function extractYoutubeVideoId(url: string): string | null {
    try {
        const parsed = new URL(url);

        if (parsed.hostname.includes('youtube.com') || parsed.hostname.includes('youtu.be')) {
            // watch?v=
            const v = parsed.searchParams.get('v');
            if (v) {
                return v;
            }

            // youtu.be/<id>
            const parts = parsed.pathname.split('/').filter(Boolean);
            if (parsed.hostname.includes('youtu.be') && parts[0]) {
                return parts[0];
            }

            // /embed/<id>
            if (parts[0] === 'embed' && parts[1]) {
                return parts[1];
            }

            // /shorts/<id>
            if (parts[0] === 'shorts' && parts[1]) {
                return parts[1];
            }
        }

        return null;
    } catch {
        return null;
    }
}
